import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary font-medium text-primary-foreground shadow-sm hover:bg-primary/90 active:bg-primary/85',
        destructive:
          'bg-destructive font-medium text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/85',
        ghost: 'text-secondary-foreground hover:bg-accent',
        link: 'text-primary underline-offset-4 hover:underline px-0 py-0',
        outline:
          'border border-brand-sage/30 bg-card text-brand-ink-soft hover:bg-brand-cream-shadow',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;
export type ButtonVariant = NonNullable<ButtonVariantProps['variant']>;
export type ButtonSize = NonNullable<ButtonVariantProps['size']>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps {
  asChild?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

function Button({
  variant,
  size,
  asChild = false,
  loading,
  leadingIcon,
  trailingIcon,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  if (asChild) {
    // Forward children to Slot — it merges the button styles onto the single
    // child (e.g. a <Link>). Without this, asChild buttons render empty.
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {children}
      </Comp>
    );
  }
  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        buttonVariants({ variant, size: variant === 'link' ? undefined : size, className }),
      )}
    >
      {loading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden
        />
      ) : leadingIcon ? (
        <span aria-hidden>{leadingIcon}</span>
      ) : null}
      <span className="inline-flex items-center gap-1.5">{children}</span>
      {trailingIcon && !loading ? <span aria-hidden>{trailingIcon}</span> : null}
    </button>
  );
}

export { Button, buttonVariants };
